import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Card } from './ui/Card';
import Button from './ui/Button';
import { api } from '../services/api';

const TokenRefreshManager = () => {
  const [tokenStatus, setTokenStatus] = useState(null);
  const [refreshHistory, setRefreshHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enqueueing, setEnqueueing] = useState(false);
  const [error, setError] = useState(null);

  const fetchTokenStatus = async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      setError(null);

      const response = await api.get('/token-refresh/status');
      if (response.data.success) {
        setTokenStatus(response.data.data);
      } else {
        setError(response.data.error || 'Failed to fetch token status');
      }
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to fetch token status';
      setError(message);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const fetchRefreshHistory = async () => {
    try {
      const response = await api.get('/token-refresh/history');
      if (response.data.success) {
        setRefreshHistory(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch refresh history:', err);
    }
  };

  const triggerManualRefresh = async () => {
    try {
      setEnqueueing(true);
      setError(null);

      const response = await api.post('/token-refresh/refresh', { scope: 'all' });

      if (response.data.success) {
        const enq = response.data.data?.enqueued ?? 0;
        const skipped = response.data.data?.skipped ?? 0;
        await fetchTokenStatus(false);
        await fetchRefreshHistory();
        toast.success(
          `Enqueued ${enq} refresh job(s)${skipped ? ` (${skipped} already queued)` : ''}. Fleet worker will drain in the background.`
        );
      } else {
        const message = response.data.error || 'Failed to refresh tokens';
        setError(message);
        toast.error(message);
      }
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to trigger token refresh';
      setError(message);
      toast.error(message);
    } finally {
      setEnqueueing(false);
    }
  };

  const fleetPending = tokenStatus?.fleetQueue?.pending ?? 0;
  const fleetProcessing = tokenStatus?.fleetQueue?.processing ?? 0;

  useEffect(() => {
    fetchTokenStatus();
    fetchRefreshHistory();
    
    const interval = setInterval(fetchTokenStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (fleetPending + fleetProcessing <= 0) return undefined;
    const fast = setInterval(() => fetchTokenStatus(false), 10000);
    return () => clearInterval(fast);
  }, [fleetPending, fleetProcessing]);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatTimeUntilExpiry = (hours, minutes) => {
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VALID': return 'text-green-600';
      case 'EXPIRED': return 'text-red-600';
      default: return 'text-slate-600';
    }
  };

  const formatRefreshType = (details) => {
    if (!details) return 'unknown';
    const parts = [details.type || 'automatic'];
    if (details.source) parts.push(details.source);
    if (details.storeName) parts.push(details.storeName);
    return parts.join(' · ');
  };

  if (loading && !tokenStatus) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-2 text-slate-600">Loading token status...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-900">Token Refresh Manager</h2>
        <Button 
          onClick={triggerManualRefresh}
          disabled={loading || enqueueing}
          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md"
        >
          {enqueueing ? 'Enqueueing…' : 'Enqueue fleet refresh'}
        </Button>
      </div>

      {tokenStatus?.fleetQueue && (
        <Card className="p-4 border border-orange-100 bg-orange-50/50">
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Fleet refresh queue</h3>
          <p className="text-sm text-slate-600">
            Pending <strong>{tokenStatus.fleetQueue.pending}</strong>
            · Processing <strong>{tokenStatus.fleetQueue.processing}</strong>
            · Done <strong>{tokenStatus.fleetQueue.done}</strong>
            · Failed <strong>{tokenStatus.fleetQueue.failed}</strong>
            {fleetPending + fleetProcessing > 0 && (
              <span className="text-orange-800"> — auto-refreshing every 10s</span>
            )}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Drains via ENABLE_FLEET_JOB_WORKER on the API host or{' '}
            <code className="bg-white px-1 rounded">node server/tools/fleet-jobs-drain.js</code>
          </p>
        </Card>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Error: {error}
        </div>
      )}

      {/* Last Refresh Info */}
      {tokenStatus?.lastRefresh && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Last Refresh</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-600">Last Refresh Time</p>
              <p className="font-medium">{formatTime(tokenStatus.lastRefresh.timestamp)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Type</p>
              <p className="font-medium">{formatRefreshType(tokenStatus.lastRefresh.details)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Status</p>
              <p className={`font-medium ${tokenStatus.lastRefresh.success ? 'text-green-600' : 'text-red-600'}`}>
                {tokenStatus.lastRefresh.success ? 'Success' : 'Failed'}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Connected Stores</p>
              <p className="font-medium">
                {tokenStatus.totalStores} total
                {tokenStatus.storePage?.hasMore ? ` (showing ${tokenStatus.stores?.length || 0})` : ''}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Store Status */}
      {tokenStatus?.stores && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Store Token Status</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Store Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Profile ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Last Refreshed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Expires At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Time Until Expiry
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {tokenStatus.stores.map((store, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {store.storeName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {store.profileId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(store.status)}`}>
                        {store.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {store.lastRefreshedAt ? formatTime(store.lastRefreshedAt) : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {store.expiresAt ? formatTime(store.expiresAt) : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {formatTimeUntilExpiry(store.hoursUntilExpiry, store.minutesUntilExpiry)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Refresh History */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Refresh History</h3>
        {refreshHistory.length === 0 ? (
          <p className="text-sm text-slate-500">No refresh events logged yet. On-demand refreshes during report runs will appear here.</p>
        ) : (
          <div className="space-y-2">
            {refreshHistory.slice(0, 20).map((entry, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-slate-50 rounded">
                <div>
                  <p className="font-medium">{formatTime(entry.timestamp)}</p>
                  <p className="text-sm text-slate-600">
                    {formatRefreshType(entry.details)}
                    {entry.details?.updatedCount != null && ` · ${entry.details.updatedCount} stores`}
                  </p>
                  {entry.details?.error && (
                    <p className="text-xs text-red-600 mt-1">{entry.details.error}</p>
                  )}
                </div>
                <div className="text-right">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    entry.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {entry.success ? 'Success' : 'Failed'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default TokenRefreshManager;
